import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import GestureIndicator from "@/components/main/GestureIndicator.vue";
import CompanyInfo from "@/components/mypage/CompanyInfo.vue";
import Tutorial from "@/components/mypage/Tutorial.vue";
import Page from "@/components/shared/Page.vue";
import ScrollTopControl from "@/components/shared/ScrollTopControl.vue";
import BaseButton from "@/components/shared/button/base/BaseButton.vue";
import SearchInput from "@/components/shared/inputs/SearchInput.vue";
import PageModal from "@/components/shared/modal/base/PageModal.vue";
import TermsDetail from "@/components/signup/TermsDetail.vue";
import { useGetGuideInfoOnLogout } from "@/composables/guide/useGetGuideInfoOnLogout";
import { INoticeInfoData } from "@/domain/entities/bbs";
import { ITermsInfo } from "@/domain/entities/serviceInfo/termsInfo";
import router from "@/router";
import { useBbsStore } from "@/stores/bbsStore";
import { useServiceStore } from "@/stores/serviceStore";
import { RouterName, TermsTypeCode } from "@/types";
import {
  useGetBottomRelativePosition,
  useGetTopRelativePosition,
} from "@/use/useGetRelativePosition";
import { computed, onActivated, onMounted, ref } from "vue";
import { RouterLink } from "vue-router";


const __default__ = {
  name: "MainLogout",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  emits: ["showNoticeModal"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

_useCssVars(_ctx => ({
  "7914c1a2-footerHeightPx": (footerHeightPx.value)
}))

const emit = __emit;

const bbsStore = useBbsStore();
const serviceStore = useServiceStore();
const serviceInfo = computed(() => serviceStore.serviceInfo);
const logoPath = computed(() => serviceInfo.value?.logoPath);
const signUpInfos = computed(() => serviceStore.signupInfos ?? []);

const isPublicLineApi = computed(() => !!serviceInfo.value?.isPublicLineApi);
const isPublicTalkApi = computed(() => !!serviceInfo.value?.isPublicTalkApi);

const header = ref<HTMLElement>();
const catchPhrase = ref<HTMLElement>();
const catchPhraseFirst = ref<HTMLElement>();
const catchPhraseSecond = ref<HTMLElement>();
const footer = ref<HTMLElement>();

const catchPhrases = computed(() => {
  const logoutHtml = serviceInfo.value?.campaigns?.find(
    (campaign) => campaign.logoutHtml?.length
  )?.logoutHtml;

  if (logoutHtml?.length) {
    return [logoutHtml];
  }

  return serviceInfo.value?.catchPhrases ?? [];
});

const statusBarHeightStr = computed(() =>
  getComputedStyle(document.documentElement).getPropertyValue(
    "--statusbar-height"
  )
);

const { guideInfo, getGuideInfoOnLogout } = useGetGuideInfoOnLogout();

const getGuideInfoIfNeeded = async () => {
  if (!isPublicTalkApi.value) return;

  getGuideInfoOnLogout();
};

const onClickSignUp = () => {
  if (signUpInfos.value.length > 1) {
    goToTypesOfSignUp();
  } else {
    goToTermsOfUse();
  }
};

const goToTypesOfSignUp = () => {
  router.push({ name: RouterName.TypesOfSignUp });
};

const goToTermsOfUse = () => {
  router.push({ name: RouterName.TermsOfUse });
};

const goToLogin = () => {
  router.push({
    name: RouterName.Login,
  });
};

const goToSearch = () => {
  router.push({ name: RouterName.LineSearch });
};

const container = ref<HTMLElement>();
const scrollToTop = () => {
  container.value?.scrollTo({
    top: 0,
    behavior: "smooth",
  });
  resetScrollEvent();
};

/** 스크롤 이벤트 처리 */
const shouldHideIndicator = ref(false);
const shouldHideCatchPhraseFirst = ref(false);
const shouldHideCatchPhraseSecond = ref(false);
const shouldFixSearchInput = ref(false);
const onScrollEvent = (event: Event) => {
  if (!isPublicLineApi.value || !isPublicTalkApi.value) return;

  const element = event.target as HTMLElement;
  const headerHeight = header.value?.offsetHeight ?? 54;
  const statusBarHeight = Number(statusBarHeightStr.value.slice(0, -2));

  shouldHideIndicator.value = element.scrollTop > 5;

  shouldHideCatchPhraseFirst.value =
    !!catchPhraseFirst.value &&
    useGetTopRelativePosition(catchPhraseFirst.value) <
      statusBarHeight + headerHeight + 16;

  shouldHideCatchPhraseSecond.value =
    !!catchPhraseSecond.value &&
    useGetTopRelativePosition(catchPhraseSecond.value) <
      statusBarHeight + headerHeight + 16;

  shouldFixSearchInput.value =
    !!catchPhrase.value &&
    useGetBottomRelativePosition(catchPhrase.value) <
      statusBarHeight + headerHeight;
};

const resetScrollEvent = () => {
  shouldHideIndicator.value = false;
  shouldHideCatchPhraseFirst.value = false;
  shouldHideCatchPhraseSecond.value = false;
  shouldFixSearchInput.value = false;
};

/** 약관 */
const termsList = ref<ITermsInfo[]>([]);
const selectedTerms = ref<ITermsInfo>();
const shouldShowTerms = ref(false);

const getTermsList = () => {
  termsList.value = serviceStore.termsList;
};

const onClickTerms = (code: TermsTypeCode) => {
  selectedTerms.value = termsList.value?.find((terms) => terms.code === code);
  toggleShowTerms(true);
};

const toggleShowTerms = (show: boolean) => {
  shouldShowTerms.value = show;
};

const noticeInfos = ref<INoticeInfoData[]>([]);

// 공지 팝업 모달 처리
const showNoticeModal = () => {
  const popupNoticeInfos = noticeInfos.value.filter(
    (noticeinfo) => noticeinfo.isPopup && !doesCookieExist(noticeinfo.id)
  );
  emit("showNoticeModal", popupNoticeInfos);
};

const doesCookieExist = (key: string) => {
  const cookieData = document.cookie.split(";");
  const index = cookieData.findIndex((cookie) => cookie.includes(key));
  return index !== -1;
};

/** 공지사항 목록 가져오기 */
const getNoticeInfos = async () => {
  if (!isPublicTalkApi.value) return;

  await bbsStore.getNoticeInfos();
  noticeInfos.value = bbsStore.noticeInfos;
  showNoticeModal();
};

onMounted(async () => {
  getGuideInfoIfNeeded();
  getTermsList();
  getNoticeInfos();
});

onActivated(() => {
  scrollToTop();
});

const footerHeightPx = computed(() =>
  footer.value?.clientHeight ? `${footer.value?.clientHeight}px` : "202px"
);

const __returned__ = { emit, bbsStore, serviceStore, serviceInfo, logoPath, signUpInfos, isPublicLineApi, isPublicTalkApi, header, catchPhrase, catchPhraseFirst, catchPhraseSecond, footer, catchPhrases, statusBarHeightStr, guideInfo, getGuideInfoOnLogout, getGuideInfoIfNeeded, onClickSignUp, goToTypesOfSignUp, goToTermsOfUse, goToLogin, goToSearch, container, scrollToTop, shouldHideIndicator, shouldHideCatchPhraseFirst, shouldHideCatchPhraseSecond, shouldFixSearchInput, onScrollEvent, resetScrollEvent, termsList, selectedTerms, shouldShowTerms, getTermsList, onClickTerms, toggleShowTerms, noticeInfos, showNoticeModal, doesCookieExist, getNoticeInfos, footerHeightPx, GestureIndicator, CompanyInfo, Tutorial, Page, ScrollTopControl, BaseButton, SearchInput, PageModal, TermsDetail, get RouterName() { return RouterName }, get RouterLink() { return RouterLink } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})